<div class="grid grid-cols-2 gap-6">
  <div class="flex flex-col gap-4">
    <!-- Surface Habitable -->
    <div>
      <div class="flex items-center gap-1 mb-1">
        <span class="text-xs font-semibold text-gray-11 uppercase tracking-wider">Surface Habitable</span>
        <mat-icon
          class="text-xs! h-3.5! w-3.5! text-gray-9 cursor-help"
          matTooltip="Surface fermée et chauffée avec hauteur sous plafond ≥ 1,80 m (Loi Carrez / Boutin)"
          matTooltipPosition="above"
        >
          help_outline
        </mat-icon>
      </div>
      @if (property().keyInfo.surface.habitable) {
        <div class="flex flex-col">
          <span class="text-2xl font-black text-indigo-9">
            {{ property().keyInfo.surface.habitable }}
            <span class="text-sm font-medium text-gray-11">m²</span>
          </span>
          @if (pricePerSquareMeter(); as priceM2) {
            <span class="text-xs font-semibold text-gray-11 mt-0.5">
              {{ priceM2 | number: '1.0-0' : 'fr-FR' }} €/m²
            </span>
          }
        </div>
      } @else {
        <span class="text-2xl font-bold text-gray-10">-</span>
      }
    </div>

    <!-- Surface au sol -->
    @if (property().keyInfo.surface.ground) {
      <div>
        <div class="flex items-center gap-1 mb-1">
          <span class="text-xs font-semibold text-gray-11 uppercase tracking-wider">Surface au Sol</span>
          <mat-icon
            class="text-xs! h-3.5! w-3.5! text-gray-9 cursor-help"
            matTooltip="Surface totale intérieure au sol incluant les parties sous combles/sous-pentes < 1,80 m"
            matTooltipPosition="above"
          >
            help_outline
          </mat-icon>
        </div>
        <span class="text-2xl font-black text-indigo-9">
          {{ property().keyInfo.surface.ground }}
          <span class="text-sm font-medium text-gray-11">m²</span>
        </span>
      </div>
    }

    <!-- Surface Terrain -->
    @if (property().keyInfo.surface.land) {
      <div>
        <div class="flex items-center gap-1 mb-1">
          <span class="text-xs font-semibold text-gray-11 uppercase tracking-wider">Surface Terrain</span>
          <mat-icon
            class="text-xs! h-3.5! w-3.5! text-gray-9 cursor-help"
            matTooltip="Superficie totale de la parcelle cadastrale (incluant bâti, cour et jardin)"
            matTooltipPosition="above"
          >
            help_outline
          </mat-icon>
        </div>
        <span class="text-2xl font-black text-indigo-9">
          {{ property().keyInfo.surface.land }}
          <span class="text-sm font-medium text-gray-11">m²</span>
        </span>
      </div>
    }
  </div>

  @if (property().keyInfo.availability) {
    <div class="col-span-2 md:col-span-1">
      <span class="text-xs font-semibold text-gray-11 uppercase tracking-wider block mb-1"
        >Disponibilité</span
      >
      <div class="text-gray-12 font-medium flex items-center gap-2">
        <mat-icon
          class="text-sm! h-4! w-4!"
          [class.text-ruby-11]="property().keyInfo.availability?.isOccupied"
          [class.text-jade-11]="!property().keyInfo.availability?.isOccupied"
        >
          {{ property().keyInfo.availability?.isOccupied ? 'lock' : 'check_circle' }}
        </mat-icon>
        {{ property().keyInfo.availability?.isOccupied ? 'Occupé' : 'Libre' }}
        @if (property().keyInfo.availability?.isOccupied && property().keyInfo.availability?.occupiedUntil) {
          <span class="text-xs font-normal text-gray-11 ml-2">
            (jusqu'au
            {{
              $safeNavigationMigration(property().keyInfo.availability?.occupiedUntil) | date: 'dd/MM/yyyy'
            }})
          </span>
        }
      </div>
      @if (property().keyInfo.availability?.saleUrgency) {
        <div class="mt-2 flex flex-col gap-1">
          <span class="text-xs text-gray-11"
            >Vente :
            {{
              $safeNavigationMigration(property().keyInfo.availability?.saleUrgency)
                | etxeEnumDisplay: 'saleUrgency'
            }}</span
          >
          @if (
            property().keyInfo.availability?.isAlreadyOnSale !== undefined &&
            property().keyInfo.availability?.isAlreadyOnSale !== null
          ) {
            <span class="text-xs text-gray-11">
              Déjà en vente : {{ property().keyInfo.availability?.isAlreadyOnSale ? 'Oui' : 'Non' }}
            </span>
            @if (
              property().keyInfo.availability?.isAlreadyOnSale &&
              property().keyInfo.availability?.currentMarketingMode
            ) {
              <span class="text-xs text-gray-11"
                >Mode :
                {{
                  $safeNavigationMigration(property().keyInfo.availability?.currentMarketingMode)
                    | etxeEnumDisplay: 'currentMarketingMode'
                }}
                @if (property().keyInfo.availability?.exclusiveMandateEndDate) {
                  <span class="text-ruby-11 font-medium">
                    (fin le
                    {{
                      $safeNavigationMigration(property().keyInfo.availability?.exclusiveMandateEndDate)
                        | date: 'dd/MM/yyyy'
                    }}
                    -
                    {{
                      property().keyInfo.availability?.exclusiveMandateEndDate
                        | etxeEnumDisplay: 'remainingDays'
                    }})
                  </span>
                }
              </span>
            }
          }
        </div>
      }
    </div>
  }

  @if (property().keyInfo.accessibility || property().keyInfo.subtype) {
    <div class="col-span-2 md:col-span-3 mt-4 pt-4 border-t border-gray-6">
      <div class="flex justify-between mb-3">
        <span class="text-xs font-semibold text-gray-11 uppercase tracking-wider block content-center"
          >Accessibilité & Bâtiment</span
        >
        @if (property().keyInfo.subtype) {
          <span
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-3 text-gray-12 border border-gray-6"
          >
            {{
              property().keyInfo.subtype | etxeEnumDisplay: 'subtype' : property().definition?.propertyType
            }}
          </span>
        }
      </div>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Configuration des étages -->
        @let floorConfig =
          property().keyInfo.accessibility?.apartment?.numberOfFloors
            | etxeEnumDisplay: 'floorConfig' : property().definition?.propertyType;
        @if (floorConfig) {
          <div class="bg-gray-2 p-3 rounded-lg border border-gray-6">
            <span class="text-xs text-gray-10 block mb-1">Configuration des étages</span>
            <span class="text-sm font-medium text-gray-12">{{ floorConfig }}</span>
          </div>
        }

        <!-- Étage appartement / Total immeuble -->
        @let apartmentFloor =
          property().keyInfo.accessibility?.apartment?.floor
            | etxeEnumDisplay: 'apartmentFloor' : property().keyInfo.accessibility?.buildingNumberOfFloors;
        @if (apartmentFloor) {
          <div class="bg-gray-2 p-3 rounded-lg border border-gray-6">
            <span class="text-xs text-gray-10 block mb-1">Étage / Immeuble</span>
            <span class="text-sm font-medium text-gray-12">{{ apartmentFloor }}</span>
          </div>
        }

        <!-- Ascenseur -->
        @if (
          property().definition?.propertyType === PropertyType.APARTMENT ||
          property().keyInfo.accessibility?.buildingHasElevator !== undefined
        ) {
          <div class="bg-gray-2 p-3 rounded-lg border border-gray-6">
            <span class="text-xs text-gray-10 block mb-1">Ascenseur</span>
            <span class="text-sm font-medium text-gray-12">{{
              property().keyInfo.accessibility?.buildingHasElevator ? 'Oui' : 'Non'
            }}</span>
          </div>
        }

        <!-- PMR -->
        @if (property().keyInfo.accessibility?.pmrCompliance !== undefined) {
          <div class="bg-gray-2 p-3 rounded-lg border border-gray-6">
            <span class="text-xs text-gray-10 block mb-1">PMR</span>
            <span class="text-sm font-medium text-gray-12">{{
              property().keyInfo.accessibility?.pmrCompliance ? 'Conforme' : 'Non'
            }}</span>
          </div>
        }
      </div>
    </div>
  }

  @if (property().keyInfo.coOwnership?.isInCoOwnership) {
    <div class="col-span-2 md:col-span-3 mt-4">
      <div
        class="p-4 bg-orange-a2 border border-orange-a4 rounded-xl flex flex-col sm:flex-row items-start gap-4"
      >
        <div class="bg-orange-a3 p-2 rounded-lg flex shrink-0">
          <mat-icon class="text-orange-9">business</mat-icon>
        </div>
        <div class="w-full">
          <h4 class="font-bold text-orange-12 m-0 mb-3 text-lg">En copropriété</h4>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm text-orange-11">
            @if (property().keyInfo.coOwnership?.numberOfLots) {
              <div>
                <span class="block text-xs uppercase tracking-wider font-semibold text-orange-9/70 mb-1"
                  >Nombre de lots</span
                >
                <span class="font-medium">{{ property().keyInfo.coOwnership?.numberOfLots }}</span>
              </div>
            }
            @if (property().keyInfo.coOwnership?.lotNumber) {
              <div>
                <span class="block text-xs uppercase tracking-wider font-semibold text-orange-9/70 mb-1"
                  >Lot(s)</span
                >
                <span class="font-medium">{{ property().keyInfo.coOwnership?.lotNumber }}</span>
              </div>
            }
            @if (property().keyInfo.coOwnership?.trustee?.name) {
              <div>
                <span class="block text-xs uppercase tracking-wider font-semibold text-orange-9/70 mb-1"
                  >Syndic</span
                >
                <span class="font-medium">{{ property().keyInfo.coOwnership?.trustee?.name }}</span>
                @if (property().keyInfo.coOwnership?.trustee?.isOnline) {
                  <span
                    class="inline-flex items-center ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-a4 text-orange-12"
                    >En ligne</span
                  >
                }
              </div>
            }
          </div>
          @if (property().keyInfo.coOwnership?.trustee?.hasOngoingProcedures) {
            <div class="w-full mt-4 p-3 bg-ruby-a2 border border-ruby-a4 rounded-lg">
              <span class="font-bold text-ruby-11 flex items-center gap-1.5 mb-2 text-sm">
                <mat-icon class="text-ruby-9 text-[18px] w-[18px] h-[18px]">warning</mat-icon>
                Procédures en cours
              </span>
              <div class="flex flex-wrap gap-2">
                @for (proc of property().keyInfo.coOwnership?.trustee?.ongoingProcedures; track proc) {
                  <span
                    class="inline-flex items-center px-2 py-1 rounded bg-ruby-a3 text-ruby-11 text-xs font-medium border border-ruby-a4"
                  >
                    {{ proc | etxeEnumDisplay: 'coOwnershipProcedureType' }}
                  </span>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>
